import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapPatientSummaries } from '@/api/mappers';
import type { PatientSummaryViewModel } from '@/models/patient';

import { useDebouncedValue } from './useDebouncedValue';
import {
  PATIENT_SEARCH_DEFAULT_LIMIT,
  PATIENT_SEARCH_MIN_LENGTH,
  type UsePatientSearchOptions,
  type UsePatientSearchResult,
} from './usePatientSearch';

const DEBOUNCE_MS = 200;

/**
 * A digit anywhere in the query is treated as an identifier lookup (MRN, NHS,
 * ULI, Epic ID, …) rather than a last name — names don't contain digits, and
 * `/persons/search` can't match both fields at once. Everything else routes to
 * `last_name`.
 */
function buildPersonSearchParams(
  query: string,
  limit: number,
): { last_name?: string; identifier_value?: string; size: number } {
  if (/\d/.test(query)) return { identifier_value: query, size: limit };
  return { last_name: query, size: limit };
}

/**
 * Server-side quick-search over `GET /persons/search` (PTV2-1232 #7). Debounces
 * the query, gates on a minimum length, and returns pre-mapped
 * `PatientSummaryViewModel`s so QuickSearch never touches raw DTOs.
 *
 * Routing: identifier-looking input (contains a digit) searches `identifier_value`,
 * otherwise `last_name` — the BFF's `/persons/search` is structured (no free-text
 * or first_name) and there is no `/search` endpoint. Searching as the user types
 * avoids shipping the whole person table to the browser; the trade-off is that
 * first-name / full-name substring matching needs a BE free-text (or first_name)
 * parameter before it can come back.
 */
export function usePersonSearch(
  rawQuery: string,
  options: UsePatientSearchOptions = {},
): UsePatientSearchResult {
  const limit = options.limit ?? PATIENT_SEARCH_DEFAULT_LIMIT;
  const debounceMs = options.debounceMs ?? DEBOUNCE_MS;

  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, debounceMs);
  const isActive = debouncedQuery.length >= PATIENT_SEARCH_MIN_LENGTH;

  const query = useQuery<readonly PatientSummaryViewModel[]>({
    queryKey: ['persons', 'search', 'last-name', debouncedQuery, limit] as const,
    queryFn: async ({ signal }) => {
      const page = await apiClient.searchPersons(
        buildPersonSearchParams(debouncedQuery, limit),
        { signal },
      );
      return mapPatientSummaries(page.content);
    },
    enabled: isActive,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const debounceSettling = trimmed !== debouncedQuery;
  const isLoading = isActive && (debounceSettling || query.isFetching);

  // Gate on `isActive` so `keepPreviousData` never leaks stale hits back into
  // QuickSearch once the input drops below the minimum length.
  return {
    results: isActive ? (query.data ?? []) : [],
    isLoading,
    error: isActive ? (query.error ?? null) : null,
  };
}
